// Service Contacts — EPIC B PR1.
// INSERT via RPC SECURITY DEFINER (pattern projet) ; SELECT/UPDATE direct.
import { supabase } from '../../lib/supabaseClient';
import type { Tables, Database } from '../../types/database.types';

export type Contact = Tables<'contacts'>;
export type DossierContact = Tables<'dossier_contacts'>;
export type ContactKind = Database['public']['Enums']['contact_kind'];
export type ContactCategory = Database['public']['Enums']['contact_category'];
export type DossierContactRole = Database['public']['Enums']['dossier_contact_role'];

export const CONTACT_CATEGORY_LABELS: Record<ContactCategory, string> = {
  client:         'Client',
  opposing_party: 'Partie adverse',
  expert:         'Expert',
  notary:         'Notaire',
  bailiff:        'Huissier',
  magistrate:     'Magistrat',
  other:          'Autre',
};

export const CONTACT_KIND_LABELS: Record<ContactKind, string> = {
  individual: 'Personne physique',
  corporate:  'Personne morale',
};

export const DOSSIER_CONTACT_ROLE_LABELS: Record<DossierContactRole, string> = {
  client:                 'Client',
  local_counsel:          'Avocat postulant',
  opposing_party:         'Partie adverse',
  opposing_counsel:       'Conseil adverse',
  court:                  'Tribunal',
  bailiff:                'Commissaire de justice',
  judicial_representative: 'Mandataire judiciaire',
  judicial_liquidator:    'Liquidateur judiciaire',
  judicial_administrator: 'Administrateur judiciaire',
  expert:                 'Expert',
  witness:                'Témoin',
  other:                  'Autre',
};

export interface ContactFilters {
  search?: string;
  kind?: ContactKind | null;
  category?: ContactCategory[];
}

export interface CreateContactInput {
  kind?: ContactKind;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  siret?: string | null;
  email?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  contact_category?: ContactCategory;
  notes?: string | null;
}

export type UpdateContactInput = Partial<CreateContactInput>;

// ─── Helpers ────────────────────────────────────────────────────────────────

export function getContactDisplayName(c: Contact): string {
  if (c.kind === 'corporate') {
    return c.company_name || 'Société sans nom';
  }
  const first = c.first_name?.trim() ?? '';
  const last = c.last_name?.trim() ?? '';
  const full = `${first} ${last}`.trim();
  return full || c.email || 'Contact sans nom';
}

// ─── SELECT ────────────────────────────────────────────────────────────────

export async function listContacts(filters: ContactFilters = {}): Promise<Contact[]> {
  let query = supabase.from('contacts').select('*');

  if (filters.kind) query = query.eq('kind', filters.kind);
  if (filters.category?.length) query = query.in('contact_category', filters.category);

  if (filters.search?.trim()) {
    const s = filters.search.trim();
    query = query.or(
      [
        `first_name.ilike.%${s}%`,
        `last_name.ilike.%${s}%`,
        `company_name.ilike.%${s}%`,
        `email.ilike.%${s}%`,
        `siret.ilike.%${s}%`,
      ].join(','),
    );
  }

  // Tri alphabétique : last_name puis company_name (NULLS LAST natif Postgres)
  query = query
    .order('last_name', { ascending: true, nullsFirst: false })
    .order('company_name', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false });

  const { data, error } = await query;
  if (error) throw new Error(`Failed to list contacts: ${error.message}`);
  return data ?? [];
}

export async function getContactById(id: string): Promise<Contact | null> {
  const { data, error } = await supabase
    .from('contacts')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(`Failed to fetch contact: ${error.message}`);
  return data;
}

export async function getContactCounts(): Promise<{ total: number; byCategory: Partial<Record<ContactCategory, number>> }> {
  const { data, error } = await supabase.from('contacts').select('contact_category');
  if (error) throw new Error(`Failed to count contacts: ${error.message}`);
  const byCategory: Partial<Record<ContactCategory, number>> = {};
  (data ?? []).forEach((row) => {
    const cat = row.contact_category as ContactCategory;
    byCategory[cat] = (byCategory[cat] ?? 0) + 1;
  });
  return { total: data?.length ?? 0, byCategory };
}

// ─── Liens dossier ↔ contact ───────────────────────────────────────────────

export interface DossierContactWithContact extends DossierContact {
  contact: Contact;
}

export async function listDossierContacts(dossierId: string): Promise<DossierContactWithContact[]> {
  const { data, error } = await supabase
    .from('dossier_contacts')
    .select('*, contact:contacts(*)')
    .eq('dossier_id', dossierId)
    .order('is_primary', { ascending: false })
    .order('added_at', { ascending: true });
  if (error) throw new Error(`Failed to list dossier_contacts: ${error.message}`);
  return (data ?? []) as unknown as DossierContactWithContact[];
}

export interface ContactDossierWithDossier {
  dossier_id: string;
  contact_id: string;
  role: DossierContactRole;
  is_primary: boolean;
  added_at: string;
  dossier: Tables<'dossiers'>;
}

export async function listContactDossiers(contactId: string): Promise<ContactDossierWithDossier[]> {
  const { data, error } = await supabase
    .from('dossier_contacts')
    .select('dossier_id, contact_id, role, is_primary, added_at, dossier:dossiers(*)')
    .eq('contact_id', contactId)
    .order('added_at', { ascending: false });
  if (error) throw new Error(`Failed to list contact dossiers: ${error.message}`);
  return (data ?? []) as unknown as ContactDossierWithDossier[];
}

// ─── MUTATIONS ─────────────────────────────────────────────────────────────

export async function createContact(input: CreateContactInput): Promise<Contact> {
  const { data, error } = await supabase.rpc('create_contact', {
    p_kind:             input.kind ?? undefined,
    p_first_name:       input.first_name ?? undefined,
    p_last_name:        input.last_name ?? undefined,
    p_company_name:     input.company_name ?? undefined,
    p_siret:            input.siret ?? undefined,
    p_email:            input.email ?? undefined,
    p_phone:            input.phone ?? undefined,
    p_address_line1:    input.address_line1 ?? undefined,
    p_address_line2:    input.address_line2 ?? undefined,
    p_postal_code:      input.postal_code ?? undefined,
    p_city:             input.city ?? undefined,
    p_country:          input.country ?? undefined,
    p_contact_category: input.contact_category ?? undefined,
    p_notes:            input.notes ?? undefined,
  });
  if (error) throw new Error(`Failed to create contact: ${error.message}`);
  if (!data) throw new Error('create_contact returned no data');
  return data as Contact;
}

export async function updateContact(id: string, patch: UpdateContactInput): Promise<Contact> {
  const { data, error } = await supabase
    .from('contacts')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(`Failed to update contact: ${error.message}`);
  return data;
}

export async function deleteContact(id: string): Promise<void> {
  const { error } = await supabase.from('contacts').delete().eq('id', id);
  if (error) throw new Error(`Failed to delete contact: ${error.message}`);
}

export async function linkContactToDossier(args: {
  dossierId: string;
  contactId: string;
  role?: DossierContactRole;
  isPrimary?: boolean;
}): Promise<DossierContact> {
  const { data, error } = await supabase.rpc('link_contact_to_dossier', {
    p_dossier_id: args.dossierId,
    p_contact_id: args.contactId,
    p_role:       args.role ?? undefined,
    p_is_primary: args.isPrimary ?? undefined,
  });
  if (error) throw new Error(`Failed to link contact: ${error.message}`);
  return data as DossierContact;
}

export async function unlinkContactFromDossier(args: {
  dossierId: string;
  contactId: string;
  role: DossierContactRole;
}): Promise<void> {
  const { error } = await supabase
    .from('dossier_contacts')
    .delete()
    .eq('dossier_id', args.dossierId)
    .eq('contact_id', args.contactId)
    .eq('role', args.role);
  if (error) throw new Error(`Failed to unlink contact: ${error.message}`);
}

/** Rattache un conseil à la partie qu'il représente dans ce dossier (null = détacher). */
export async function setDossierContactRepresents(args: {
  dossierId: string;
  contactId: string;
  role: DossierContactRole;
  representsContactId: string | null;
}): Promise<void> {
  const { error } = await supabase
    .from('dossier_contacts')
    .update({ represents_contact_id: args.representsContactId })
    .eq('dossier_id', args.dossierId)
    .eq('contact_id', args.contactId)
    .eq('role', args.role);
  if (error) throw new Error(`Failed to set represented party: ${error.message}`);
}

// ============================================================
// B-05 — Détection de doublons (fuzzy)
// ============================================================

export interface SimilarContactCandidate {
  id: string;
  kind: ContactKind;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  postal_code: string | null;
  city: string | null;
  match_reason: 'email' | 'siret' | 'phone' | 'name+cp';
  score: number;
}

export interface SimilarContactInput {
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  siret?: string | null;
  postal_code?: string | null;
  phone?: string | null;
  exclude_id?: string | null;
}

function hasEnoughInputToSearch(input: SimilarContactInput): boolean {
  // On déclenche dès qu'on a UN signal exploitable
  return (
    !!input.email?.trim() ||
    !!input.siret?.trim() ||
    !!input.phone?.trim() ||
    (!!input.postal_code?.trim() &&
      (!!input.first_name?.trim() ||
        !!input.last_name?.trim() ||
        !!input.company_name?.trim()))
  );
}

export async function findSimilarContacts(
  input: SimilarContactInput,
): Promise<SimilarContactCandidate[]> {
  if (!hasEnoughInputToSearch(input)) return [];

  const { data, error } = await supabase.rpc('find_similar_contacts', {
    p_email: input.email?.trim() || undefined,
    p_first_name: input.first_name?.trim() || undefined,
    p_last_name: input.last_name?.trim() || undefined,
    p_company_name: input.company_name?.trim() || undefined,
    p_siret: input.siret?.trim() || undefined,
    p_postal_code: input.postal_code?.trim() || undefined,
    p_phone: input.phone?.trim() || undefined,
    p_exclude_id: input.exclude_id || undefined,
  });

  if (error) throw new Error(`find_similar_contacts: ${error.message}`);
  return (data ?? []) as SimilarContactCandidate[];
}
